/**
 * Type definitions for the Phase-02 ReportRequest / triage stepper.
 */

import type { ProvenanceRef } from './attachment';
import {
  transformToBackofficeSync,
  transformToBackofficeSyncMarker,
  type ApiBackofficeSync,
  type BackofficeSync,
  type BackofficeSyncMarker,
} from './backoffice-sync';
import { registryDocumentsOrderable, type RegistryDocumentsScope } from './registry-documents';
import type { ReportLifecycleStage } from './report';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

/**
 * One extracted report-request candidate, as produced by the
 * `extract_report_requests` graph. Lives on the Enquiry; consumed by
 * EnquiryDetail for the convert-to-requests flow.
 */
export interface ExtractedCandidate {
  report_config_slug?: string | null;
  report_config_id?: string | null;
  subjects?: string[];
  jurisdictions?: string[];
  expedited?: boolean;
  confidence?: ConfidenceLevel;
  reasoning?: string;
  /** Provenance — which conversation inputs this candidate was derived
   *  from. Populated by the /extract endpoint (anywhere-in-conversation
   *  extraction); absent on the legacy initial-classify artifact. */
  sources?: ProvenanceRef[];
}

export interface ExtractedCandidatesPayload {
  candidates?: ExtractedCandidate[];
  needs_human_review?: boolean;
  overall_reasoning?: string;
}

export type TriageStage =
  | 'initial'
  | 'subjects_extracted'
  | 'subjects_identified'
  | 'plan_built'
  | 'np_selected'
  | 'ready_to_start'
  | 'started'
  | 'rejected';

export type TriageActorKind = 'analyst' | 'system';

/**
 * Render `ReportRequest.source` as a human-readable label.
 *
 * The DB stores short slugs (`freshdesk`, `gti_hub`); analysts see
 * "Freshdesk", "GTI Hub", etc. Unknown sources fall back to a
 * title-cased version of the slug so we never blow up on a new value.
 */
export function formatRequestSource(source: string | null | undefined): string {
  if (!source) {
    return '(unknown)';
  }
  const labels: Record<string, string> = {
    freshdesk: 'Freshdesk',
    gti_hub: 'GTI Hub',
  };
  if (labels[source]) {
    return labels[source];
  }
  return source
    .split(/[_-]/)
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');
}

/** Ordered tuple of stages — used by the stepper to compute the next stage. */
export const TRIAGE_STAGES: TriageStage[] = [
  'initial',
  'subjects_extracted',
  'subjects_identified',
  'plan_built',
  'np_selected',
  'ready_to_start',
  'started',
];

export function nextTriageStage(
  stage: TriageStage,
  stages: TriageStage[] = TRIAGE_STAGES
): TriageStage | null {
  const idx = stages.indexOf(stage);
  if (idx < 0 || idx === stages.length - 1) {
    return null;
  }
  return stages[idx + 1];
}

export function isStageBefore(
  a: TriageStage,
  b: TriageStage,
  stages: TriageStage[] = TRIAGE_STAGES
): boolean {
  return stages.indexOf(a) < stages.indexOf(b);
}

/** Display label for a stage in the stepper. */
export function triageStageLabel(stage: TriageStage): string {
  switch (stage) {
    case 'initial':
      return 'Initial';
    case 'subjects_extracted':
      return 'Subjects';
    case 'subjects_identified':
      return 'Identification';
    case 'plan_built':
      return 'Plan';
    case 'np_selected':
      return 'NP';
    case 'ready_to_start':
      return 'Ready';
    case 'started':
      return 'Started';
    case 'rejected':
      return 'Rejected';
  }
}

// ---------------------------------------------------------------------------
// Workspace sections — the display grammar of the triage workspace.
//
// Stages map onto SECTIONS of one scrolling page. `plan_built` is
// invisible (folded into Partners): Identification → Partners is an
// FE-chained double advance, and a request parked at `plan_built`
// renders the Partners section as active. The enum itself is untouched.
// ---------------------------------------------------------------------------

export type TriageSectionSlug = 'define' | 'subjects' | 'identification' | 'partners' | 'launch';

const SECTION_FOR_STAGE: Partial<Record<TriageStage, TriageSectionSlug>> = {
  initial: 'define',
  subjects_extracted: 'subjects',
  subjects_identified: 'identification',
  plan_built: 'partners',
  np_selected: 'partners',
  ready_to_start: 'launch',
};

export function sectionForStage(stage: TriageStage): TriageSectionSlug | null {
  return SECTION_FOR_STAGE[stage] ?? null;
}

export const SECTION_LABEL: Record<TriageSectionSlug, string> = {
  define: 'Define',
  subjects: 'Subjects',
  identification: 'Identification',
  partners: 'Partners',
  launch: 'Start',
};

export interface WorkspaceSection {
  slug: TriageSectionSlug;
  label: string;
  /** The triage stages this section spans (Partners spans plan_built +
   * np_selected on the screening flow). */
  stages: TriageStage[];
}

/**
 * Derive the workspace's section list from the server-computed
 * `effective_stages` — legacy flows drop Identification (and plan_built
 * never appears in their order). Terminal stages (started/rejected)
 * contribute no section; a straggler stage outside the map is ignored.
 */
export function workspaceSectionsFor(effectiveStages: TriageStage[]): WorkspaceSection[] {
  const sections: WorkspaceSection[] = [];
  for (const stage of effectiveStages) {
    const slug = SECTION_FOR_STAGE[stage];
    if (!slug) {
      continue;
    }
    const last = sections[sections.length - 1];
    if (last && last.slug === slug) {
      last.stages.push(stage);
    } else {
      sections.push({ slug, label: SECTION_LABEL[slug], stages: [stage] });
    }
  }
  return sections;
}

// ---------------------------------------------------------------------------
// Gates — read-only exit-condition evaluation (GET /report-requests/{id}/gates)
// ---------------------------------------------------------------------------

export type TriageGateSeverity = 'blocking' | 'info';

export interface TriageGateBlocker {
  code: string;
  label: string;
  /** Workspace section slug that owns the fix — chips deep-link here. */
  target: string;
  severity: TriageGateSeverity;
  /** Structured ids for targeted rendering (subject_id, assignment_id,
   * missing_item_ids, …). */
  refs: Record<string, unknown>;
}

export interface TriageGatesResponse {
  stage: TriageStage;
  next_stage: TriageStage | null;
  legacy_flow: boolean;
  blockers: TriageGateBlocker[];
}

export interface TriageStageHistoryEntry {
  id: string;
  fromStage: TriageStage | null;
  toStage: TriageStage;
  actorUserId: string | null;
  actorKind: TriageActorKind;
  notes: string | null;
  occurredAt: string;
}

export interface ApiTriageStageHistoryEntry {
  id: string;
  from_stage: TriageStage | null;
  to_stage: TriageStage;
  actor_user_id: string | null;
  actor_kind: TriageActorKind;
  notes: string | null;
  occurred_at: string;
}

export interface ReportRequestSummary {
  id: string;
  triageStage: TriageStage;
  source: string;
  teamId: string | null;
  freshdeskTicketId: string | null;
  requesterEmail: string | null;
  reportConfigId: string | null;
  expedited: boolean;
  /** Composed codename `{base}-{pepper}-{suffix}`. NULL for
   * GTI-Hub-direct RRs without a parent enquiry (the codename machinery
   * can't compose without an inherited pepper). */
  codename: string | null;
  /** Family key (parent enquiry's codename) for PATCH /codenames — never
   * derive it by splitting `codename`. */
  codenameFamily: string | null;
  createdAt: string | null;
}

/**
 * Raw webhook body stored on the ReportRequest.
 *
 * Freshdesk outbound webhooks don't have a fixed schema — admins build
 * the body from `{{ticket.*}}` placeholders in an "Advanced" JSON
 * template. Only `id` is guaranteed; everything else depends on what
 * the admin chose to include. The component must tolerate any subset.
 */
export interface FreshdeskRawPayload {
  id: number;
  subject?: string | null;
  description?: string | null;
  description_text?: string | null;
  requester_email?: string | null;
  status?: string | number | null;
  priority?: string | number | null;
  custom_fields?: Record<string, unknown>;
  tags?: string[];
  [key: string]: unknown;
}

export interface ClassificationResult {
  is_report_request?: boolean;
  confidence?: 'high' | 'medium' | 'low';
  reasoning?: string;
}

export interface ReportRequestDetail {
  id: string;
  triageStage: TriageStage;
  source: string;
  teamId: string | null;
  enquiryId: string | null;
  freshdeskTicketId: string | null;
  requesterEmail: string | null;
  /** The client's own reference (matter / PO number), client-edited. */
  clientReference: string | null;
  /** Raw currency override (null = config's); `currency` is effective. */
  priceCurrency: string | null;
  /** Persisted: the last push of these commercials to Backoffice failed. */
  backofficeSyncMarker: BackofficeSyncMarker;
  /** Outcome of the push the last edit triggered (the edit's own response). */
  backofficeSync: BackofficeSync | null;
  /** Effective due (override ?? started_at + TAT) and the raw override. */
  dueAt: string | null;
  dueAtOverride: string | null;
  requiresClientActivationOverride: boolean | null;
  /** Hub-owned billing flags for the case's Backoffice project (POC /
   * R&D / excluded from billing) — admin-editable, synced on write. */
  isProofOfConcept: boolean;
  isResearchAndDevelopment: boolean;
  isExcludedFromBilling: boolean;
  /** Explicit extraction file pick (null = automatic provenance
   * scoping; [] = typed lines only) + the resolved effective set. */
  extractionSourceIds: string[] | null;
  effectiveExtractionAttachmentIds: string[];
  rawPayload: FreshdeskRawPayload | null;
  classificationResult: ClassificationResult | null;
  reportConfigId: string | null;
  subjectStrings: string[] | null;
  turnaroundTimeDays: number | null;
  price: string | null;
  expedited: boolean;
  rejectedAt: string | null;
  rejectionReason: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  reportId: string | null;
  reportLifecycleStage: ReportLifecycleStage | null;
  /** Composed codename (same rules as the summary). */
  codename: string | null;
  /** Family key for PATCH /codenames — never derive by splitting `codename`. */
  codenameFamily: string | null;
  /** Display name of the picked report config (null when none picked). */
  configName: string | null;
  /** ISO 4217 code from the picked config (USD when none picked). */
  currency: string;
  /** Legacy (manual / Backoffice docx) flow snapshot. */
  legacyFlow: boolean;
  /** Server-computed stage list this RR actually walks — legacy RRs skip
   * identification/plan-build. Drives the stepper + revert options. */
  effectiveStages: TriageStage[];
  /** Whether Armadillo i-doc is locked on a live partner assignment of
   * the request's report, and whether that report already has orders -
   * together they gate the registry-documents card on the Partners step
   * and its new orders. Available, with no orders, when the API sends
   * nothing (an API without the gate). */
  registryDocuments: RegistryDocumentsScope;
  history: TriageStageHistoryEntry[];
}

// API (snake_case) mirrors
export interface ApiReportRequestSummary {
  id: string;
  triage_stage: TriageStage;
  source: string;
  team_id: string | null;
  freshdesk_ticket_id: string | null;
  requester_email: string | null;
  report_config_id: string | null;
  expedited: boolean;
  codename: string | null;
  codename_family?: string | null;
  created_at: string | null;
}

/** POST /report-requests/{id}/split result — trimmed original + new siblings. */
export interface ApiTriageSplitResponse {
  original: ApiReportRequestSummary;
  created: ApiReportRequestSummary[];
}

export interface ApiReportRequestDetail {
  id: string;
  triage_stage: TriageStage;
  source: string;
  team_id: string | null;
  enquiry_id: string | null;
  freshdesk_ticket_id: string | null;
  requester_email: string | null;
  /** The client's own reference (matter / PO number), client-edited. */
  client_reference?: string | null;
  /** Raw currency override (null = config's); `currency` is effective. */
  price_currency?: string | null;
  backoffice_sync_error?: string | null;
  backoffice_sync_failed_at?: string | null;
  backoffice_sync?: ApiBackofficeSync | null;
  /** Effective due (override ?? started_at + TAT) and the raw override. */
  due_at?: string | null;
  due_at_override?: string | null;
  requires_client_activation_override?: boolean | null;
  is_proof_of_concept?: boolean;
  is_research_and_development?: boolean;
  is_excluded_from_billing?: boolean;
  /** Which attached files feed subject extraction: the raw explicit
   * pick (null = automatic provenance scoping; [] = typed lines only)
   * and the resolved effective set the next advance will use. */
  extraction_source_ids?: string[] | null;
  effective_extraction_attachment_ids?: string[];
  raw_payload: FreshdeskRawPayload | null;
  classification_result: ClassificationResult | null;
  report_config_id: string | null;
  subject_strings: string[] | null;
  turnaround_time_days: number | null;
  price: string | null;
  expedited: boolean;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_at: string | null;
  updated_at: string | null;
  report_id: string | null;
  report_lifecycle_stage: ReportLifecycleStage | null;
  codename?: string | null;
  codename_family?: string | null;
  config_name?: string | null;
  currency?: string;
  legacy_flow?: boolean;
  effective_stages?: TriageStage[];
  registry_documents?: RegistryDocumentsScope;
  history: ApiTriageStageHistoryEntry[];
}

export function transformToTriageHistoryEntry(
  api: ApiTriageStageHistoryEntry
): TriageStageHistoryEntry {
  return {
    id: api.id,
    fromStage: api.from_stage,
    toStage: api.to_stage,
    actorUserId: api.actor_user_id,
    actorKind: api.actor_kind,
    notes: api.notes,
    occurredAt: api.occurred_at,
  };
}

export function transformToReportRequestSummary(
  api: ApiReportRequestSummary
): ReportRequestSummary {
  return {
    id: api.id,
    triageStage: api.triage_stage,
    source: api.source,
    teamId: api.team_id,
    freshdeskTicketId: api.freshdesk_ticket_id,
    requesterEmail: api.requester_email,
    reportConfigId: api.report_config_id,
    expedited: api.expedited,
    codename: api.codename ?? null,
    codenameFamily: api.codename_family ?? null,
    createdAt: api.created_at,
  };
}

export function transformToReportRequestDetail(api: ApiReportRequestDetail): ReportRequestDetail {
  return {
    id: api.id,
    triageStage: api.triage_stage,
    source: api.source,
    teamId: api.team_id,
    enquiryId: api.enquiry_id,
    freshdeskTicketId: api.freshdesk_ticket_id,
    requesterEmail: api.requester_email,
    clientReference: api.client_reference ?? null,
    priceCurrency: api.price_currency ?? null,
    backofficeSyncMarker: transformToBackofficeSyncMarker(
      api.backoffice_sync_error,
      api.backoffice_sync_failed_at
    ),
    backofficeSync: transformToBackofficeSync(api.backoffice_sync),
    dueAt: api.due_at ?? null,
    dueAtOverride: api.due_at_override ?? null,
    requiresClientActivationOverride: api.requires_client_activation_override ?? null,
    isProofOfConcept: api.is_proof_of_concept ?? false,
    isResearchAndDevelopment: api.is_research_and_development ?? false,
    isExcludedFromBilling: api.is_excluded_from_billing ?? false,
    extractionSourceIds: api.extraction_source_ids ?? null,
    effectiveExtractionAttachmentIds: api.effective_extraction_attachment_ids ?? [],
    rawPayload: api.raw_payload,
    classificationResult: api.classification_result,
    reportConfigId: api.report_config_id,
    subjectStrings: api.subject_strings,
    turnaroundTimeDays: api.turnaround_time_days,
    price: api.price,
    expedited: api.expedited,
    rejectedAt: api.rejected_at,
    rejectionReason: api.rejection_reason,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
    reportId: api.report_id,
    reportLifecycleStage: api.report_lifecycle_stage,
    codename: api.codename ?? null,
    codenameFamily: api.codename_family ?? null,
    configName: api.config_name ?? null,
    currency: api.currency ?? 'USD',
    legacyFlow: api.legacy_flow ?? false,
    effectiveStages: api.effective_stages?.length ? api.effective_stages : TRIAGE_STAGES,
    registryDocuments: {
      available: registryDocumentsOrderable(api.registry_documents),
      has_orders: api.registry_documents?.has_orders ?? false,
    },
    history: (api.history ?? []).map(transformToTriageHistoryEntry),
  };
}

// Mutation payloads

export interface TriageInitialUpdateRequest {
  team_id?: string | null;
  report_config_id?: string | null;
  subject_strings?: string[] | null;
  turnaround_time_days?: number | null;
  price?: string | null;
  expedited?: boolean | null;
}

/**
 * Body for `PATCH /report-requests/{id}/pricing` — the dedicated late-stage
 * pricing endpoint. Unlike `/triage/initial`, this stays editable at every
 * stage except `started` and `rejected` (those 409 with "pricing is locked").
 * All fields optional, but the backend rejects a fully-empty body; send only
 * the fields being changed (unspecified ones are preserved).
 */
/**
 * PATCH /report-requests/{id}/pricing body. Admins can edit commercials
 * at every stage (started included); every write syncs to the Backoffice
 * project. `turnaround_time_days` (relative to Start) and `due_at`
 * (fixed) are mutually exclusive — setting one clears the other.
 */
export interface PricingUpdateRequest {
  price?: string | null;
  turnaround_time_days?: number | null;
  expedited?: boolean | null;
  /** ISO 4217 per-report override; explicit null clears to the config's. */
  currency?: 'GBP' | 'EUR' | 'USD' | null;
  /** Concrete due date beating started_at + TAT; explicit null clears. */
  due_at?: string | null;
  /** Per-report client-activation override; explicit null = config decides. */
  requires_client_activation?: boolean | null;
  /** Hub-owned billing flags (NOT NULL — null means skip). */
  is_proof_of_concept?: boolean | null;
  is_research_and_development?: boolean | null;
  is_excluded_from_billing?: boolean | null;
  /** Why the commercials are changing. Required (10+ characters) once the
   * report is completed — its figures may already have been billed. */
  reason?: string | null;
}

export interface TriageAdvanceRequest {
  target: TriageStage;
}

export interface TriageRevertRequest {
  to_stage: TriageStage;
  reason: string;
}

export interface TriageRejectRequest {
  reason: string;
}

export interface TriageStartResponse {
  report_request_id: string;
  report_id: string;
  triage_stage: TriageStage;
  report_lifecycle_stage: ReportLifecycleStage;
}

export interface TriageEditSubjectRequest {
  subject_name?: string | null;
  subject_type?: string | null;
  reference?: string | null;
  additional_info?: Record<string, unknown> | null;
  jurisdiction_codes?: string[] | null;
}

/**
 * Body for `POST /report-requests/{id}/triage/subjects` — add a subject by
 * hand at the `subjects_extracted` stage (one the client forgot to name, or
 * that extraction missed). Same vocabulary as the edit body; the name is
 * required.
 */
export interface TriageAddSubjectRequest {
  subject_name: string;
  subject_type?: string | null;
  reference?: string | null;
  additional_info?: Record<string, unknown> | null;
  jurisdiction_codes?: string[] | null;
}

/**
 * Body for `POST /report-requests/{id}/triage/subjects/{sid}/manual` — resolve a
 * subject the identification graph couldn't find, from analyst-entered data.
 * Allowed only at the `subjects_identified` stage; resolves the subject (the
 * backend synthesises a selected candidate from this record) so triage can
 * advance. Only `subject_name` + `subject_type` are required. The body itself is
 * strict (unknown top-level keys → 422), but `additional_info` is permissive —
 * unknown sub-keys are kept, so unstructured detail can go under `uncategorised`.
 */
export interface TriageManualResolveRequest {
  subject_name: string;
  /** 'Company' | 'Person' | 'Vessel'. */
  subject_type: string;
  jurisdiction_codes?: string[] | null;
  reference?: string | null;
  additional_info?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Client-facing report requests (the /requests tab)
// ---------------------------------------------------------------------------

/** Coarse client-language lifecycle for a request. `started` only ever
 * displays for the transient started-with-no-visible-report edge — once
 * a report exists its coarse stage is hoisted over this value. */
export type ClientRequestStatus = 'submitted' | 'started' | 'closed';

export const CLIENT_REQUEST_STATUS_META: Record<
  ClientRequestStatus,
  { label: string; color: string }
> = {
  submitted: { label: 'Submitted', color: 'yellow' },
  started: { label: 'Started', color: 'blue' },
  closed: { label: 'Closed', color: 'gray' },
};

/** Coarse client-facing status of the linked report (mirrors
 * `ClientReportStage` in types/report.ts). */
export type ClientLinkedReportStatus =
  | 'submitted'
  | 'pending_activation'
  | 'in_progress'
  | 'awaiting_profile_review'
  | 'completed';

/** Badge meta for hoisted linked-report statuses — colors match the
 * client Reports table so the two lists read identically. */
export const CLIENT_LINKED_REPORT_STATUS_META: Record<
  ClientLinkedReportStatus,
  { label: string; color: string }
> = {
  submitted: { label: 'Submitted', color: 'yellow' },
  pending_activation: { label: 'Pending activation', color: 'orange' },
  in_progress: { label: 'In progress', color: 'blue' },
  // The client's own action: confirm the social media profiles.
  awaiting_profile_review: { label: 'Profiles need confirming', color: 'orange' },
  completed: { label: 'Complete', color: 'green' },
};

/** The status a request row DISPLAYS: the linked report's coarse stage
 * once a client-visible report exists, else the request-level status.
 * The list filter speaks this vocabulary too (server-side twin:
 * `_apply_status`). */
export function displayedRequestStatus(request: {
  status: ClientRequestStatus;
  reportStatus?: ClientLinkedReportStatus | null;
}): { label: string; color: string } {
  return request.reportStatus
    ? CLIENT_LINKED_REPORT_STATUS_META[request.reportStatus]
    : CLIENT_REQUEST_STATUS_META[request.status];
}

/** Wire values accepted by the Requests list `status` filter — the
 * DISPLAYED vocabulary (hoisted report stages + closed), not the raw
 * row status. Server-side twin: `ClientStatusFilter`. */
export type ClientRequestStatusFilter =
  | 'submitted'
  | 'pending_activation'
  | 'in_progress'
  | 'awaiting_profile_review'
  | 'completed'
  | 'closed';

export const CLIENT_REQUEST_FILTER_OPTIONS: {
  value: ClientRequestStatusFilter;
  label: string;
}[] = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_activation', label: 'Pending activation' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'awaiting_profile_review', label: 'Profiles need confirming' },
  { value: 'completed', label: 'Complete' },
  { value: 'closed', label: 'Closed' },
];

/** Wire shape of GET /client/report-requests[/{id}]. */
export interface ApiClientReportRequestSummary {
  id: string;
  /** 'request' rows are report requests; 'enquiry' rows are the
   * caller-visible enquiries unioned into the same list (they hand
   * over to converted requests via the shared codename family). */
  kind?: 'request' | 'enquiry';
  codename: string | null;
  status: ClientRequestStatus;
  subjects: string[];
  config_name: string | null;
  requester_email: string | null;
  /** The client's own reference (matter / PO number), client-editable. */
  client_reference?: string | null;
  expedited: boolean;
  turnaround_time_days: number | null;
  price: string | number | null;
  /** Null when pricing is stripped (client analysts) or on enquiry rows. */
  currency: string | null;
  report_id: string | null;
  report_status?: ClientLinkedReportStatus | null;
  due_at?: string | null;
  jurisdictions?: string[];
  created_at: string | null;
}

export interface ClientReportRequestSummary {
  id: string;
  kind: 'request' | 'enquiry';
  codename: string | null;
  status: ClientRequestStatus;
  subjects: string[];
  configName: string | null;
  requesterEmail: string | null;
  /** The client's own reference (matter / PO number), client-editable. */
  clientReference: string | null;
  expedited: boolean;
  turnaroundTimeDays: number | null;
  price: string | number | null;
  /** Null when pricing is stripped (client analysts) or on enquiry rows. */
  currency: string | null;
  reportId: string | null;
  /** Coarse status of the linked report — `pending_activation` is the
   * request page's cue to offer "Start screening". */
  reportStatus: ClientLinkedReportStatus | null;
  /** Due date once screening starts (started_at + effective TAT). */
  dueAt: string | null;
  /** Jurisdiction names (identified subjects, else the confirmed
   * payload's codes). */
  jurisdictions: string[];
  createdAt: string | null;
}

/** Where a case share is anchored. A share reaches everything below it,
 * so a case is shared at its top: the enquiry when there is one (every
 * request and report raised from it follows), else the request (its
 * reports follow), else — for a self-served report — the report itself. */
export interface ShareAnchor {
  kind: 'enquiry' | 'request' | 'report';
  id: string;
}

/** Where a listed share sits relative to the case being viewed: on it
 * (`direct`, removable here), or on the request / enquiry above it and
 * reaching it by inheritance (managed where it sits). */
export type ShareSource = 'direct' | 'request' | 'enquiry';

/** Wire shape of a `/participants` row (enquiry, request or report). */
export interface ApiCaseParticipant {
  id: string;
  email: string;
  created_at: string | null;
  source?: ShareSource;
}

/** A teammate a case is shared with (visibility for restricted client
 * roles — project managers / analysts see own + shared cases only). */
export interface CaseParticipant {
  id: string;
  email: string;
  createdAt: string | null;
  source: ShareSource;
}

export function transformToCaseParticipant(api: ApiCaseParticipant): CaseParticipant {
  return {
    id: api.id,
    email: api.email,
    createdAt: api.created_at,
    source: api.source ?? 'direct',
  };
}

/** Wire shape of /client/report-requests/{id}/team-members rows. */
export interface ApiClientTeamMember {
  user_id: number;
  first_name: string;
  last_name: string;
  email: string | null;
  client_role: string | null;
}

/** A client-side member of the request's team — the sharing picker's
 * option pool (sourced live from the Backoffice membership system). */
export interface ClientTeamMember {
  userId: number;
  firstName: string;
  lastName: string;
  email: string | null;
  clientRole: string | null;
}

export function transformToClientTeamMember(api: ApiClientTeamMember): ClientTeamMember {
  return {
    userId: api.user_id,
    firstName: api.first_name ?? '',
    lastName: api.last_name ?? '',
    email: api.email,
    clientRole: api.client_role ?? null,
  };
}

export function transformToClientReportRequestSummary(
  api: ApiClientReportRequestSummary
): ClientReportRequestSummary {
  return {
    id: api.id,
    kind: api.kind ?? 'request',
    codename: api.codename,
    status: api.status,
    subjects: api.subjects ?? [],
    configName: api.config_name,
    requesterEmail: api.requester_email,
    clientReference: api.client_reference ?? null,
    expedited: api.expedited,
    turnaroundTimeDays: api.turnaround_time_days,
    price: api.price,
    currency: api.currency,
    reportId: api.report_id,
    reportStatus: api.report_status ?? null,
    dueAt: api.due_at ?? null,
    jurisdictions: api.jurisdictions ?? [],
    createdAt: api.created_at,
  };
}
